import React, { useState } from 'react';
import CameraswitchRoundedIcon from '@mui/icons-material/CameraswitchRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import DeselectRoundedIcon from '@mui/icons-material/DeselectRounded';
import Forward5RoundedIcon from '@mui/icons-material/Forward5Rounded';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import Replay5RoundedIcon from '@mui/icons-material/Replay5Rounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import SkipPreviousRoundedIcon from '@mui/icons-material/SkipPreviousRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import ZoomOutRoundedIcon from '@mui/icons-material/ZoomOutRounded';

import { useAutoRepeat } from '../../../helpers/useAutoRepeat';
import styles from './MobileControlCenter.module.scss';

type TMobileControlCenter = {
  currentTime: number;
  duration: number;
  playing: boolean;
  muted: boolean;
  selectedCount: number;
  onPlayPause: () => void;
  onMuteToggle: () => void;
  onPreviousClip: () => void;
  onNextClip: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onSeek: (seconds: number) => void;
  onDeselect: () => void;
  onLocatePlayhead: () => void;
  onShowComments: () => void;
  onOpenChange?: (open: boolean) => void;
  canSwitchAngle: boolean;
  followPlayhead: boolean;
  zoomInDisabled?: boolean;
  zoomOutDisabled?: boolean;
};

export const MOBILE_SWITCH_ANGLE_EVENT = 'amplify:mobile-switch-angle';

const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), max);

const formatTime = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;

  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
    : `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

const MobileControlCenter: React.FC<TMobileControlCenter> = ({
  currentTime,
  duration,
  playing,
  muted,
  selectedCount,
  onPlayPause,
  onMuteToggle,
  onPreviousClip,
  onNextClip,
  onZoomIn,
  onZoomOut,
  onSeek,
  onDeselect,
  onLocatePlayhead,
  onShowComments,
  onOpenChange,
  canSwitchAngle,
  followPlayhead,
  zoomInDisabled,
  zoomOutDisabled,
}) => {
  const [open, setOpen] = useState(true);
  const safeDuration = Number.isFinite(duration) ? Math.max(0, duration) : 0;
  const safeCurrentTime = Number.isFinite(currentTime) ? clamp(currentTime, safeDuration) : 0;
  const playbackDisabled = safeDuration <= 0;
  const selectionDisabled = selectedCount <= 0;
  const zoomOutHandlers = useAutoRepeat(onZoomOut, { disabled: zoomOutDisabled });
  const zoomInHandlers = useAutoRepeat(onZoomIn, { disabled: zoomInDisabled });

  const seekBy = (seconds: number) => {
    onSeek(clamp(safeCurrentTime + seconds, safeDuration));
  };

  const setControlCenterOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  return (
    <div className={styles.root}>
      {!open && (
        <button
          className={styles.toggle}
          type="button"
          aria-label="Open control center"
          onClick={() => setControlCenterOpen(true)}
        >
          <TuneRoundedIcon />
        </button>
      )}

      <section className={`${styles.sheet} ${open ? styles.sheetOpen : ''}`} aria-hidden={!open}>
        <div className={styles.heading}>
          <strong>Control Center</strong>
          <div className={styles.headingActions}>
            <button type="button" onClick={onShowComments}>
              <ChatBubbleOutlineRoundedIcon />
              Comments
            </button>
            <button type="button" onClick={() => setControlCenterOpen(false)}>
              Hide
            </button>
          </div>
        </div>

        <div className={styles.controls}>
          <div className={styles.playbackControls}>
            <button type="button" disabled={selectionDisabled} onClick={onPreviousClip}>
              <SkipPreviousRoundedIcon />
              <span>Prev</span>
            </button>
            <button type="button" disabled={playbackDisabled} onClick={() => seekBy(-5)}>
              <Replay5RoundedIcon />
              <span>-5 sec</span>
            </button>
            <button className={styles.playButton} type="button" disabled={playbackDisabled} onClick={onPlayPause}>
              {playing ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
              <span>{playing ? 'Pause' : 'Play'}</span>
            </button>
            <button type="button" disabled={playbackDisabled} onClick={() => seekBy(5)}>
              <Forward5RoundedIcon />
              <span>+5 sec</span>
            </button>
            <button type="button" disabled={selectionDisabled} onClick={onNextClip}>
              <SkipNextRoundedIcon />
              <span>Next</span>
            </button>
          </div>
          <div className={styles.utilityControls}>
            <button type="button" disabled={zoomOutDisabled} data-preserve-clip-edit {...zoomOutHandlers}>
              <ZoomOutRoundedIcon />
              <span>Zoom Out</span>
            </button>
            <button type="button" disabled={zoomInDisabled} data-preserve-clip-edit {...zoomInHandlers}>
              <ZoomInRoundedIcon />
              <span>Zoom In</span>
            </button>
            <button
              className={followPlayhead ? styles.followButtonActive : undefined}
              type="button"
              aria-pressed={followPlayhead}
              onClick={onLocatePlayhead}
            >
              <MyLocationRoundedIcon />
              <span>Locate</span>
            </button>
            <button
              type="button"
              disabled={!canSwitchAngle}
              onClick={() => window.dispatchEvent(new Event(MOBILE_SWITCH_ANGLE_EVENT))}
            >
              <CameraswitchRoundedIcon />
              <span>Switch Angle</span>
            </button>
            <button type="button" aria-pressed={muted} onClick={onMuteToggle}>
              {muted ? <VolumeOffRoundedIcon /> : <VolumeUpRoundedIcon />}
              <span>{muted ? 'Unmute' : 'Mute'}</span>
            </button>
            <button className={styles.deselectButton} type="button" disabled={selectionDisabled} onClick={onDeselect}>
              <DeselectRoundedIcon />
              <span>Deselect</span>
            </button>
          </div>
        </div>

        <div className={styles.seek}>
          <span>{formatTime(safeCurrentTime)}</span>
          <input
            aria-label="Jump to video position"
            type="range"
            min={0}
            max={safeDuration || 0.1}
            step={0.1}
            value={safeCurrentTime}
            disabled={playbackDisabled}
            onChange={(event) => onSeek(Number(event.target.value))}
          />
          <span>{formatTime(safeDuration)}</span>
        </div>
      </section>
    </div>
  );
};

export default MobileControlCenter;
